// A declined save or autopay charge shows the LAWPAY_DECLINED sentence as-is. A failed plan installment shows last_error the same way. Delivered 2026-11-01.
// Saving a bank account shows 8am's eCheck authorization (firm name filled in) and sends it as authorization_text. Turning autopay on for a saved bank account sends that same sentence. A bank:false or bank_unavailable config is shown, not hidden. Delivered 2026-11-01.
// pkg198 — client Payment methods: LawPay hosted fields (token only), the
// card-on-file consent sentence from GET /api/client-pay/methods/config, an
// autopay toggle (recurring consent required to turn on), and the client's
// payment-plan view. Saving a method is refused unless LawPay is in test mode.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { bankUnavailableMessage, echeckAuthorizationFromConfig } from '../utils/echeckAuthorization';
import { lawpayChargeError } from '../utils/lawpayChargeError';
import { useFirmName } from '../contexts/FirmSettingsContext';

const HF_SRC = 'https://cdn.affinipay.com/hostedfields/1.5.3/fieldGen_1.5.3.js';
const F = { fontFamily: 'Arial, sans-serif' };
const SERIF = { fontFamily: 'Georgia, serif' };
const NAVY = '#0F1838';
const card = { background: '#fff', border: '1px solid #e3ddd0', borderRadius: 8, padding: '16px 18px', marginBottom: 16 };
const hfBox = { border: '1px solid #c9ced6', borderRadius: 4, height: 40, padding: '0 10px', background: '#fff', overflow: 'hidden', position: 'relative', boxSizing: 'border-box' };
const hfInputCss = { 'font-family': 'Arial, sans-serif', 'font-size': '15px', color: '#1a1a1a', border: 'none', outline: 'none', 'box-shadow': 'none', height: '38px', 'line-height': '38px', width: '100%', padding: '0', margin: '0', background: 'transparent' };
const HF_IFRAME_CSS = `
  .lp-hf iframe { display:block !important; width:100% !important; height:38px !important; border:0 !important; margin:0 !important; padding:0 !important; background:transparent !important; }
  .lp-hf { line-height: 0; }
`;

const money = n => '$' + (+n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = s => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-').map(Number);
  if (d.length !== 3 || !d[2]) return String(s);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d[1] - 1]} ${d[2]}, ${d[0]}`;
};

const INST_STATUS = {
  scheduled: 'Scheduled',
  processing: 'Processing',
  paid: 'Paid',
  failed: 'Failed',
  exhausted: 'Failed',
  link_sent: 'Link sent',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
};
const PLAN_STATUS = { active: 'Active', paused: 'Paused', cancelled: 'Cancelled', completed: 'Completed' };
const FREQ = { weekly: 'Weekly', biweekly: 'Every two weeks', monthly: 'Monthly' };

function methodLabel(m) {
  if (!m) return '—';
  const brand = m.brand || (m.type === 'bank' ? 'Bank account' : 'Card');
  const exp = m.type === 'card' && m.exp_month && m.exp_year
    ? ` · exp ${String(m.exp_month).padStart(2, '0')}/${m.exp_year}`
    : '';
  return `${brand} ••${m.last4}${exp}`;
}

function timingSentence(autopay) {
  if (!autopay) return '';
  if (autopay.timing === 'days_after_issue') {
    const n = Number(autopay.days_after_issue) || 0;
    const unit = n === 1 ? 'day' : 'days';
    return `New invoices are charged ${n} ${unit} after the issue date.`;
  }
  return 'New invoices are charged on the due date.';
}

let hfLoading = null;
function loadHostedFields() {
  if (window.AffiniPay && window.AffiniPay.HostedFields) return Promise.resolve();
  if (hfLoading) return hfLoading;
  hfLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = HF_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { hfLoading = null; reject(new Error('Could not load the secure payment form. Check your connection and try again.')); };
    document.head.appendChild(s);
  });
  return hfLoading;
}

function hostedError(ex) {
  let msg = 'Please check the payment details.';
  try {
    const p = window.AffiniPay.HostedFields.parseException(ex);
    msg = (p.messages && p.messages[0]) || p.message || msg;
  } catch (_) { /* library message unavailable */ }
  return msg;
}

// Card number, CVV, routing number, and account number live only in LawPay
// iframes. The portal sends the hosted-fields token, the method type, and the
// consent sentence — never those fields.
function SaveMethodForm({ kind, publicKey, consentText, authorizationText, me, onSaved, onCancel }) {
  const [name, setName] = useState(me.name || '');
  const [email, setEmail] = useState(me.email || '');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [postal, setPostal] = useState('');
  const [holder, setHolder] = useState('individual');
  const [given, setGiven] = useState('');
  const [surname, setSurname] = useState('');
  const [business, setBusiness] = useState('');
  const [accountType, setAccountType] = useState('CHECKING');
  const [agreed, setAgreed] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const hfRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadHostedFields();
        if (cancelled) return;
        const fields = kind === 'bank'
          ? [
            { selector: '#lp-save-routing', input: { type: 'routing_number', placeholder: 'Routing number' } },
            { selector: '#lp-save-account', input: { type: 'bank_account_number', placeholder: 'Account number' } },
          ]
          : [
            { selector: '#lp-save-number', input: { type: 'credit_card_number', placeholder: 'Card number', format: true } },
            { selector: '#lp-save-cvv', input: { type: 'cvv', placeholder: 'CVV' } },
          ];
        hfRef.current = window.AffiniPay.HostedFields.initializeFields({
          publicKey,
          input: { css: hfInputCss },
          fields,
        }, () => {});
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, publicKey]);

  async function submit() {
    setErr('');
    if (kind === 'bank') {
      if (!authorizationText || !authorizationText.includes('\u201cSubmit Payment\u201d')) {
        return setErr('The bank authorization text is not available. Refresh and try again.');
      }
    } else {
      if (!agreed) return setErr('Check the box to accept the authorization.');
      if (!consentText) return setErr('The authorization text is not available. Refresh and try again.');
    }
    let tokenArgs;
    if (kind === 'bank') {
      if (holder === 'business') {
        if (!business.trim()) return setErr('Account name is required.');
        tokenArgs = { type: 'bank', account_holder_type: 'business', name: business.trim(), account_type: accountType };
      } else {
        if (!given.trim() || !surname.trim()) return setErr('First and last name are required.');
        tokenArgs = {
          type: 'bank',
          account_holder_type: 'individual',
          given_name: given.trim(),
          surname: surname.trim(),
          name: `${given.trim()} ${surname.trim()}`,
          account_type: accountType,
        };
      }
    } else {
      if (!name.trim()) return setErr('Name on card is required.');
      if (!/^\d{1,2}$/.test(expMonth) || +expMonth < 1 || +expMonth > 12) return setErr('Enter the expiration month (1–12).');
      if (!/^\d{4}$/.test(expYear)) return setErr('Enter the 4-digit expiration year.');
      if (!postal.trim()) return setErr('Billing ZIP code is required.');
      tokenArgs = {
        name: name.trim(),
        postal_code: postal.trim(),
        exp_month: expMonth,
        exp_year: expYear,
        email: email.trim() || undefined,
      };
    }
    setBusy(true);
    try {
      let tok;
      try {
        tok = await hfRef.current.getPaymentToken(tokenArgs);
      } catch (ex) {
        throw new Error(hostedError(ex));
      }
      if (!tok || !tok.id) throw new Error('LawPay did not return a payment token.');
      const saved = await api.clientPaySaveMethod({
        token: tok.id,
        method: kind,
        consent: true,
        consent_text: kind === 'bank' ? authorizationText : consentText,
        ...(kind === 'bank' ? { authorization_text: authorizationText } : {}),
      });
      onSaved(saved);
    } catch (e) {
      setErr(lawpayChargeError(e) || e.message);
    } finally {
      setBusy(false);
    }
  }

  const lbl = { ...F, fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4 };
  const inp = { ...F, fontSize: 15, width: '100%', boxSizing: 'border-box', height: 40, border: '1px solid #c9ced6', borderRadius: 4, padding: '0 10px' };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eee' }}>
      <style>{HF_IFRAME_CSS}</style>
      <p style={{ ...F, fontSize: 12, color: '#666', margin: '0 0 12px' }}>
        {kind === 'bank' ? 'Routing and account numbers' : 'The card number and security code'} are entered in fields served by LawPay. This site receives only a token.
      </p>
      {err && <div className="alert alert-error">{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {kind === 'card' ? (
          <>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Name on card</label><input style={inp} value={name} onChange={e => setName(e.target.value)} autoComplete="cc-name" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Card number</label><div id="lp-save-number" className="lp-hf" style={hfBox} /></div>
            <div><label style={lbl}>Exp. month</label><input style={inp} value={expMonth} onChange={e => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="MM" inputMode="numeric" autoComplete="cc-exp-month" /></div>
            <div><label style={lbl}>Exp. year</label><input style={inp} value={expYear} onChange={e => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="YYYY" inputMode="numeric" autoComplete="cc-exp-year" /></div>
            <div><label style={lbl}>CVV</label><div id="lp-save-cvv" className="lp-hf" style={hfBox} /></div>
            <div><label style={lbl}>Billing ZIP</label><input style={inp} value={postal} onChange={e => setPostal(e.target.value)} autoComplete="postal-code" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Receipt email</label><input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          </>
        ) : (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Account holder</label>
              <select style={inp} value={holder} onChange={e => setHolder(e.target.value)}>
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </div>
            {holder === 'business' ? (
              <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Name on account</label><input style={inp} value={business} onChange={e => setBusiness(e.target.value)} /></div>
            ) : (
              <>
                <div><label style={lbl}>First name</label><input style={inp} value={given} onChange={e => setGiven(e.target.value)} autoComplete="given-name" /></div>
                <div><label style={lbl}>Last name</label><input style={inp} value={surname} onChange={e => setSurname(e.target.value)} autoComplete="family-name" /></div>
              </>
            )}
            <div>
              <label style={lbl}>Account type</label>
              <select style={inp} value={accountType} onChange={e => setAccountType(e.target.value)}>
                <option value="CHECKING">Checking</option>
                <option value="SAVINGS">Savings</option>
              </select>
            </div>
            <div><label style={lbl}>Routing number</label><div id="lp-save-routing" className="lp-hf" style={hfBox} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Account number</label><div id="lp-save-account" className="lp-hf" style={hfBox} /></div>
          </>
        )}
      </div>
      {kind === 'bank' ? (
        <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <p style={{ ...F, fontSize: 12, lineHeight: 1.45, color: '#333', margin: 0, flex: '1 1 280px' }}>{authorizationText}</p>
          <div className="btn-row" style={{ margin: 0, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !ready}>
              {busy ? <><span className="spinner" />Saving…</> : (ready ? 'Submit Payment' : 'Loading secure form…')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <label style={{ ...F, fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14, lineHeight: 1.45 }}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{consentText}</span>
          </label>
          <div className="btn-row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !ready}>
              {busy ? <><span className="spinner" />Saving…</> : (ready ? 'Save payment method' : 'Loading secure form…')}
            </button>
          </div>
        </>
      )}
      <p style={{ ...F, fontSize: 11, color: '#999', margin: '10px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        Secured by <img src="/lawpay-logo.svg" alt="LawPay" style={{ height: 14 }} /> · test mode
      </p>
    </div>
  );
}

export default function ClientPaymentMethodsPage() {
  const portalFirm = useFirmName();
  const [methods, setMethods] = useState([]);
  const [autopay, setAutopay] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [me, setMe] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(null);
  const [autopayMethodId, setAutopayMethodId] = useState('');
  const [autopayAgreed, setAutopayAgreed] = useState(false);
  const [changingMethod, setChangingMethod] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [methodRows, autopayRow, planRows] = await Promise.all([
      api.clientPayMethods(),
      api.clientPayAutopay(),
      api.clientPayPlans(),
    ]);
    const rows = Array.isArray(methodRows) ? methodRows : [];
    setMethods(rows);
    setAutopay(autopayRow);
    setPlans(Array.isArray(planRows) ? planRows : []);
    setAutopayMethodId(prev => {
      if (prev && rows.some(m => m.id === prev)) return prev;
      return (autopayRow && autopayRow.payment_method_id) || (rows[0] && rows[0].id) || '';
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    api.me().then(setMe).catch(() => {});
    api.clientPayMethodsConfig()
      .then(cfg => { if (!cancelled) setConfig(cfg); })
      .catch(e => { if (!cancelled) setConfigError(e.message || 'Online payments are not available.'); });
    return () => { cancelled = true; };
  }, [load]);

  async function refresh() {
    setErr('');
    try { await load(); }
    catch (e) { setErr(e.message); }
  }

  async function removeMethod(m) {
    const autopayNote = autopay && autopay.payment_method_id === m.id
      ? ' Autopay is using this method and will turn off.'
      : '';
    if (!window.confirm(`Remove ${methodLabel(m)}?${autopayNote}`)) return;
    setBusy(true); setNotice(''); setErr('');
    try {
      await api.clientPayDeleteMethod(m.id);
      setNotice('Payment method removed.');
      await refresh();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function setAutopayEnabled(enabled) {
    setErr(''); setNotice('');
    if (enabled) {
      const sentence = (autopay && autopay.consent_text) || (config && config.consent && config.consent.recurring) || '';
      if (!autopayAgreed) return setErr('Check the box to authorize automatic charges.');
      if (!sentence) return setErr('The authorization text is not available. Refresh and try again.');
      if (!autopayMethodId) return setErr('Choose a saved payment method.');
      const chosen = methods.find(m => m.id === autopayMethodId);
      const bankAuth = chosen && chosen.type === 'bank'
        ? echeckAuthorizationFromConfig(config, portalFirm)
        : '';
      if (chosen && chosen.type === 'bank' && (!bankAuth || !bankAuth.includes('\u201cSubmit Payment\u201d'))) {
        return setErr('The bank authorization text is not available. Refresh and try again.');
      }
      setBusy(true);
      try {
        const row = await api.clientPaySetAutopay({
          enabled: true,
          payment_method_id: autopayMethodId,
          consent: true,
          consent_text: sentence,
          ...(bankAuth ? { authorization_text: bankAuth } : {}),
        });
        setAutopay(row);
        setChangingMethod(false);
        setAutopayAgreed(false);
        setNotice('Autopay is on.');
      } catch (e) { setErr(lawpayChargeError(e) || e.message); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const row = await api.clientPaySetAutopay({ enabled: false });
      setAutopay(row);
      setNotice('Autopay is off.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function openPlan(id) {
    if (plan && plan.id === id) { setPlan(null); return; }
    setErr('');
    try { setPlan(await api.clientPayPlan(id)); }
    catch (e) { setErr(e.message); }
  }

  const h2 = { ...SERIF, fontSize: 18, margin: '0 0 8px', color: NAVY };
  const canAddCard = config && config.card && config.card_public_key;
  const canAddBank = config && config.bank && config.bank_public_key && !bankUnavailableMessage(config);
  const bankNotice = bankUnavailableMessage(config);
  const consentText = config && config.consent && config.consent.card_on_file;
  const authorizationText = echeckAuthorizationFromConfig(config, portalFirm);
  const recurringText = (autopay && autopay.consent_text) || (config && config.consent && config.consent.recurring) || '';
  const autopayBank = methods.find(m => m.id === autopayMethodId);
  const autopayBankAuth = autopayBank && autopayBank.type === 'bank' ? authorizationText : '';
  const showAutopayForm = autopay && (!autopay.enabled || changingMethod);

  if (loading) return <p style={F}>Loading…</p>;

  return (
    <div>
      <h1 style={{ ...SERIF, fontSize: 26, margin: '0 0 4px', color: NAVY }}>Payment methods &amp; autopay</h1>
      <p style={{ ...F, fontSize: 13, color: '#666', margin: '0 0 16px' }}>
        Save a card or bank account for future payments, turn autopay on or off, and review any payment plan.
        Invoices you pay yourself stay on <Link to="/billing">Invoices &amp; Payments</Link>.
      </p>

      {config && config.mode === 'test' && (
        <div className="alert alert-success" style={{ marginBottom: 14 }}>
          LawPay is in test mode. Saving a payment method does not charge it, and no real money moves.
        </div>
      )}
      {err && <div className="alert alert-error">{err}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <section style={card}>
        <h2 style={h2}>Saved payment methods</h2>
        {configError && <p style={{ ...F, fontSize: 13, color: '#8a1c1c' }}>{configError}</p>}
        {methods.length === 0 ? (
          <p style={{ ...F, fontSize: 13, color: '#666' }}>No saved card or bank account yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {methods.map(m => (
              <li key={m.id} style={{ ...F, fontSize: 14, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0ece3' }}>
                <span>
                  <strong>{methodLabel(m)}</strong>
                  <span style={{ color: '#888', marginLeft: 8 }}>{m.type === 'bank' ? 'Bank account' : 'Card'}</span>
                  {autopay && autopay.enabled && autopay.payment_method_id === m.id && <span style={{ marginLeft: 8, fontSize: 12, color: '#1e6b35' }}>Used for autopay</span>}
                </span>
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => removeMethod(m)}>Remove</button>
              </li>
            ))}
          </ul>
        )}
        {bankNotice && <div className="alert alert-error" role="status" style={{ marginTop: 12 }}>{bankNotice}</div>}
        {!adding && (canAddCard || canAddBank) && (
          <div className="btn-row" style={{ marginTop: 12 }}>
            {canAddCard && <button type="button" className="btn btn-sm btn-primary" onClick={() => setAdding('card')}>Add a card</button>}
            {canAddBank && <button type="button" className="btn btn-sm" onClick={() => setAdding('bank')}>Add a bank account</button>}
          </div>
        )}
        {adding && (
          <SaveMethodForm
            key={adding}
            kind={adding}
            publicKey={adding === 'bank' ? config.bank_public_key : config.card_public_key}
            consentText={consentText}
            authorizationText={authorizationText}
            me={me}
            onCancel={() => setAdding(null)}
            onSaved={() => { setAdding(null); setNotice(adding === 'bank' ? 'Bank account saved.' : 'Card saved.'); refresh(); }}
          />
        )}
      </section>

      <section style={card}>
        <h2 style={h2}>Autopay</h2>
        <p style={{ ...F, fontSize: 14, margin: '0 0 8px' }}>
          Autopay is <strong>{autopay && autopay.enabled ? 'on' : 'off'}</strong>.
          {autopay && autopay.enabled && autopay.method ? <> Charges use <strong>{methodLabel(autopay.method)}</strong>.</> : null}
        </p>
        <p style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>{timingSentence(autopay)} That schedule is set by the firm.</p>
        {autopay && autopay.replenishment && autopay.replenishment.enabled && (
          <p style={{ ...F, fontSize: 13, color: '#555' }}>
            The firm may also charge the saved method to replenish trust when the balance is below {money(autopay.replenishment.threshold)}, adding {money(autopay.replenishment.amount)}. That charge deposits to trust.
          </p>
        )}
        {showAutopayForm && (
          <>
            {methods.length === 0 ? (
              <p style={{ ...F, fontSize: 13 }}>Save a payment method before turning autopay on.</p>
            ) : (
              <>
                <div style={{ ...F, fontSize: 13, marginBottom: 8 }}>Payment method</div>
                {methods.map(m => (
                  <label key={m.id} style={{ ...F, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <input type="radio" name="autopay-method" checked={autopayMethodId === m.id} onChange={() => setAutopayMethodId(m.id)} />
                    {methodLabel(m)}
                  </label>
                ))}
                <label style={{ ...F, fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, lineHeight: 1.45 }}>
                  <input type="checkbox" checked={autopayAgreed} onChange={e => setAutopayAgreed(e.target.checked)} style={{ marginTop: 3 }} />
                  <span>{recurringText}</span>
                </label>
                {autopayBankAuth && (
                  <p style={{ ...F, fontSize: 12, lineHeight: 1.45, color: '#333', margin: '12px 0 0' }}>{autopayBankAuth}</p>
                )}
                <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} disabled={busy} onClick={() => setAutopayEnabled(true)}>
                  {autopay && autopay.enabled ? 'Update autopay' : 'Turn autopay on'}
                </button>
              </>
            )}
          </>
        )}
        {autopay && autopay.enabled && !changingMethod && (
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button type="button" className="btn" disabled={busy} onClick={() => setChangingMethod(true)}>Use a different method</button>
            <button type="button" className="btn" disabled={busy} onClick={() => setAutopayEnabled(false)}>Turn autopay off</button>
          </div>
        )}
      </section>

      <section style={card}>
        <h2 style={h2}>Payment plans</h2>
        <p style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>
          When autopay is on, an installment is charged to the saved method on its due date. When autopay is off, the firm emails a LawPay link instead.
        </p>
        {plans.length === 0 ? <p style={{ ...F, fontSize: 13, color: '#666' }}>No payment plan on file.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', ...F, fontSize: 13 }}>
              <thead>
                <tr>
                  {['Started', 'Total', 'Installment', 'Frequency', 'Status', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#777', padding: '6px 8px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id}>
                    <td style={{ padding: '8px' }}>{fmtDate(p.start_date)}</td>
                    <td style={{ padding: '8px' }}>{money(p.total)}</td>
                    <td style={{ padding: '8px' }}>{money(p.installment_amount)}</td>
                    <td style={{ padding: '8px' }}>{FREQ[p.frequency] || p.frequency}</td>
                    <td style={{ padding: '8px' }}>{PLAN_STATUS[p.status] || p.status}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <button type="button" className="btn btn-sm" onClick={() => openPlan(p.id)}>{plan && plan.id === p.id ? 'Hide' : 'View'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {plan && (
          <div style={{ marginTop: 14 }}>
            <div style={{ ...F, fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
              {FREQ[plan.frequency] || plan.frequency} plan · {money(plan.total)} · {PLAN_STATUS[plan.status] || plan.status}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', ...F, fontSize: 13 }}>
              <thead>
                <tr>
                  {['#', 'Date', 'Amount', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#777', padding: '6px 8px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(plan.installments || []).map(inst => (
                  <tr key={inst.id}>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{inst.sequence}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{fmtDate(inst.due_date)}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>{money(inst.amount)}</td>
                    <td style={{ padding: '8px', borderTop: '1px solid #f0ece3' }}>
                      {INST_STATUS[inst.status] || inst.status}
                      {inst.last_error ? <div style={{ color: '#8a1c1c', marginTop: 4 }}>{inst.last_error}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
